/**
 * The layout, as one small store (Zustand, `standards.md` §2.2): the pure
 * transitions of `layout.ts`, bound to state. The shell follows the window's
 * width into `setIsWide`.
 *
 * Each change keeps every open tile's params and tab for its widget, and an
 * open with nothing given takes them back (#74, `recall.ts`).
 */
import { create } from "zustand";

import {
  closeWidget,
  collapseExpanded,
  expandWidget,
  initialState,
  nowAt,
  openWidget,
  patchTile,
  setIsWide,
  swapTiles,
  WIDE_THRESHOLD,
  type Layout,
  type Open,
  type Slot,
  type Tile,
} from "./layout";
import { keep, recall } from "./recall";

interface LayoutStore extends Layout {
  open(open: Open): void;
  close(tile: Slot): void;
  expand(tile: Slot): void;
  collapse(): void;
  swap(): void;
  setWide(isWide: boolean): void;
  patch(tile: Slot, patch: Partial<Pick<Tile, "params" | "tab">>): void;
  /** For a test: start again. */
  reset(isWide: boolean): void;
}

const wideNow = () =>
  typeof window !== "undefined" && window.matchMedia?.(`(min-width: ${WIDE_THRESHOLD}px)`).matches;

export const useLayout = create<LayoutStore>()((set) => {
  const apply = (change: (state: Layout) => Layout) =>
    set((state) => {
      const next = change(state);
      for (const tile of next.tiles)
        if (tile) keep(tile.widget, { params: tile.params, tab: tile.tab });
      return next;
    });
  return {
    ...initialState({ isWide: Boolean(wideNow()) }),
    open: (open) =>
      apply((s) => {
        const name = nowAt(open.name);
        const given = open.params != null || open.tab != null;
        const last = given ? null : recall(name);
        return openWidget(s, {
          ...open,
          name,
          params: last?.params ?? open.params,
          tab: last?.tab ?? open.tab,
        });
      }),
    close: (tile) => apply((s) => closeWidget(s, { tile })),
    expand: (tile) => apply((s) => expandWidget(s, { tile })),
    collapse: () => apply(collapseExpanded),
    swap: () => apply(swapTiles),
    setWide: (isWide) => apply((s) => setIsWide(s, isWide)),
    patch: (tile, patch) => apply((s) => patchTile(s, tile, patch)),
    reset: (isWide) => set(initialState({ isWide })),
  };
});

/** Follow the window across the 600 px line. Returns the unsubscribe. */
export function followWidth(): () => void {
  const query = window.matchMedia?.(`(min-width: ${WIDE_THRESHOLD}px)`);
  if (!query) return () => undefined;
  const onChange = () => useLayout.getState().setWide(query.matches);
  query.addEventListener("change", onChange);
  onChange();
  return () => query.removeEventListener("change", onChange);
}
